import { Injectable } from '@angular/core';
import { FiDateFormatOptions, FiTimeFormatOptions } from '../utils/format-date';
import { AppTextService } from '../app-text';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

const minute = 60;
const hour = minute * 60;
const day = hour * 24;
const week = day * 7;
const month = day * 30;
const year = day * 365;
const calculateDelta = function (now, date) {
    return Math.round(Math.abs(now - date) / 1000);
};

@Injectable()
export class RelativeDateService {
    constructor(
        private appTextService: AppTextService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    getRelativeDate(date, now) {
        if (!now) {
            now = new Date();
        }

        if (!(date instanceof Date)) {
            date = new Date(date);
        }

        var delta = null;

        delta = calculateDelta(now, date);

        if (delta > day && delta < week) {
            date = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
            delta = calculateDelta(now, date);
        }

        var translate = (translatePhrase, timeValue?) => {
            var key;

            if (translatePhrase === 'justNow') {
                key = translatePhrase;
            } else if (now >= date) {
                key = translatePhrase + 'Ago';
            } else {
                key = translatePhrase + 'FromNow';
            }

            const translation = this.appTextService.get('relativeDate.' + key);
            return translation.replace('{{time}}', timeValue);
        };

        switch (false) {
            case !(delta < 30):
                return translate('justNow');

            case !(delta < minute):
                return translate('seconds', delta);

            case !(delta < 2 * minute):
                return translate('aMinute');

            case !(delta < hour):
                return translate('minutes', Math.floor(delta / minute));

            case Math.floor(delta / hour) !== 1:
                return translate('anHour');

            case !(delta < day):
                return translate('hours', Math.floor(delta / hour));

            case !(delta < day * 2):
                return translate('aDay');

            case !(delta < week):
                return translate('days', Math.floor(delta / day));

            case Math.floor(delta / week) !== 1:
                return translate('aWeek');

            case !(delta < month):
                return translate('weeks', Math.floor(delta / week));

            case Math.floor(delta / month) !== 1:
                return translate('aMonth');

            case !(delta < year):
                return translate('months', Math.floor(delta / month));

            case Math.floor(delta / year) !== 1:
                return translate('aYear');

            default:
                return translate('overAYear');
        }
    }

    getDateFromNowTo(date, now?) {
        if (!now) {
            now = new Date();
        }
        if (!(date instanceof Date)) {
            date = new Date(date);
        }
        var delta = calculateDelta(now, date);

        var translate = (translatePhrase, timeValue?) => {
            const translation = this.appTextService.get('checklist.n_' + translatePhrase);
            return translation.replace('{n}', timeValue);
        };

        if (delta < hour) return translate('minutes', Math.floor(delta / minute));

        if (delta < day) return translate('hours', Math.floor(delta / hour));

        return translate('days', Math.floor(delta / day));
    }

    getDateForSourceMessage(dateStr: string): string {
        if (!dateStr) return '';

        const date = new Date(dateStr);
        const now = new Date();
        now.setHours(0, 0, 0);
        let date2 = new Date(dateStr);
        date2.setHours(0, 0, 0);
        const delta2 = calculateDelta(now, date2);
        const time: string = this.fiFormatDateService.formatDate(date, FiTimeFormatOptions);

        if (delta2 < day) {
            return this.appTextService.get('today') + ', ' + time;
        }

        if (delta2 < day * 2) {
            return this.appTextService.get('yesterday') + ', ' + time;
        }

        return this.fiFormatDateService.formatDate(date, FiDateFormatOptions) + ', ' + time;
    }

    getDateForChatLatestActivity(dateStr: string): string {
        if (!dateStr) return '';

        const date = new Date(dateStr);
        const now = new Date();
        now.setHours(0, 0, 0);
        let date2 = new Date(dateStr);
        date2.setHours(0, 0, 0);
        const delta2 = calculateDelta(now, date2);

        if (delta2 < day) {
            return this.fiFormatDateService.formatDate(date, FiTimeFormatOptions);
        }

        if (delta2 < day * 2) {
            return this.appTextService.get('yesterday');
        }

        return this.fiFormatDateService.formatDate(date, FiDateFormatOptions);
    }

    getDateForUserLatestActivity(date: Date): string {
        const now = new Date();
        const delta = calculateDelta(now, date);

        if (delta < minute) return this.appTextService.get('common.right_now');

        if (delta < minute * 30) return this.appTextService.get('common.few_minutes_ago');

        if (delta < minute * 60) return this.appTextService.get('common.hour_ago');

        if (delta < minute * 60 * 4) return this.appTextService.get('common.few_hours_ago');

        // https://chainformation.atlassian.net/browse/FI-5356
        // compare dates without hours/minutes/seconds
        //date = new Date(new Date().getTime() - 62 * hour * 1000); // for tests

        let now2 = new Date(now.getTime());
        now2.setHours(0, 0, 0);
        let date2 = new Date(date.getTime());
        date2.setHours(0, 0, 0);
        const delta2 = calculateDelta(now2, date2);

        if (delta2 < day) {
            return this.appTextService.get('today');
        }

        if (delta2 < day * 2) {
            return this.appTextService.get('yesterday');
        }

        if (delta2 < day * 7) {
            const nowDayOfWeek = this.getNormalizedDayOfWeek(now);
            const dateDayOfWeek = this.getNormalizedDayOfWeek(date);
            if (dateDayOfWeek <= nowDayOfWeek) {
                return this.appTextService.get('common.this_week');
            }
        }

        return this.fiFormatDateService.formatDate(date, FiDateFormatOptions);
    }

    private getNormalizedDayOfWeek(date: Date) {
        const dayOfWeek = date.getDay();
        return dayOfWeek !== 0 ? dayOfWeek : 7;
    }
}
